import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Moon, Sun, Mountain } from "lucide-react";
import { useTheme } from "next-themes";
import { Button } from "./ui/button";
import { useSite, resolveImg } from "../context/SiteContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/journal", label: "Travel Journal" },
  { to: "/packages", label: "Packages" },
  { to: "/destinations", label: "Destinations" },
  { to: "/gallery", label: "Gallery" },
  { to: "/#reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const { brand } = useSite();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-background/70 border-b border-border" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          {brand.logo ? (
            <img src={resolveImg(brand.logo)} alt={brand.name} className="h-10 w-10 rounded-full object-cover" />
          ) : (
            <span className="h-10 w-10 grid place-items-center rounded-full bg-primary text-primary-foreground">
              <Mountain className="h-4 w-4" />
            </span>
          )}
          <div className="leading-tight">
            <div className="font-display text-lg tracking-tight">{brand.name}</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              {brand.tagline}
            </div>
          </div>
        </Link>

        <ul className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <li key={l.label}>
              <NavLink
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide hover:text-primary transition-colors ${
                    isActive && l.to === location.pathname ? "text-primary" : "text-foreground/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <button
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="h-10 w-10 grid place-items-center rounded-full border border-border hover:bg-muted transition-colors"
          >
            <Sun className="h-4 w-4 dark:hidden" />
            <Moon className="h-4 w-4 hidden dark:block" />
          </button>
          <Link to="/packages" className="hidden md:block">
            <Button data-testid="nav-plan-trip" className="rounded-full">Plan My Trip</Button>
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden h-10 w-10 grid place-items-center rounded-full border border-border"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl">
          <ul className="px-6 py-6 grid gap-4">
            {links.map((l) => (
              <li key={l.label}>
                <Link to={l.to} className="text-base" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/packages">
                <Button className="w-full rounded-full" data-testid="mobile-plan-trip">Plan My Trip</Button>
              </Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
